import { css } from "@emotion/css";
import { CopyIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius } from "@octopusdeploy/design-system-tokens";
import type React from "react";
import { useEffect, useState } from "react";
import { ActionButton, ActionButtonType } from "../../components/Button";
import { Tooltip } from "../../components/Tooltip";
interface CopyToClipboardProps {
    value: string;
    showHoverTooltip?: boolean;
    onClick?: () => void;
    children?: React.ReactNode;
}
export function CopyToClipboardButton({ value, children, showHoverTooltip = true, onClick }: CopyToClipboardProps) {
    const [event, setEvent] = useState<React.MouseEvent | null>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    useEffect(() => {
        if (event) {
            setShowConfirmation(true);
            const timerId = window.setTimeout(() => setShowConfirmation(false), 3500);
            return () => {
                window.clearTimeout(timerId);
            };
        }
    }, [event]);
    const handleClick = (e: React.MouseEvent) => {
        const copyText = async () => {
            await navigator.clipboard.writeText(value);
            setEvent(e);
        };
        copyText();
        onClick?.();
    };
    // Setting the <ToolTip /> `trigger` prop as `undefined` uses the default value: `mouseenter focus`
    const outerTooltipTrigger = showConfirmation ? "manual" : undefined;
    const innerTooltipOpen = showConfirmation ? true : false;
    return (<span>
            <Tooltip content="Copy to clipboard" trigger={outerTooltipTrigger} open={isTooltipHoverable(showHoverTooltip && !showConfirmation)}>
                <Tooltip content="Copied!" trigger="manual" open={innerTooltipOpen}>
                    <span>
                        {children ? (<span onClick={handleClick}>{children}</span>) : (<div className={copyButtonWrapper}>
                                <ActionButton onClick={handleClick} label={""} icon={<CopyIcon size={24}/>} aria-label={`Copy to clipboard`} type={ActionButtonType.Ternary}/>
                            </div>)}
                    </span>
                </Tooltip>
            </Tooltip>
        </span>);
}
function isTooltipHoverable(showHoverTooltip?: boolean) {
    // Used to set the `open` prop of <Tooltip /> passed into the `open` prop of <TippyTooltip />
    // <TippyTooltip /> is from react-tippy (https://github.com/tvkhoa/react-tippy)
    // Setting the `open` prop as `undefined` leaves interactions (e.g., hover) and the component logic to determine the open or closed status of the tooltip
    // The values `false` or `true` force the tooltip to be closed or open respectively
    return showHoverTooltip ? undefined : false;
}
const copyButtonWrapper = css({
    "&& .MuiButton-root": {
        aspectRatio: "1",
        padding: "0",
        borderRadius: borderRadius.circle,
        minWidth: "auto",
    },
    "&& .MuiButton-root .MuiButton-startIcon": {
        margin: "0",
    },
});
